import {ZonedDateTime} from "@variocube/app-ui";
import {B2Booking} from "../../data/client.gen";

/**
 * Picks the caller's currently-active booking on a given site, or undefined if none.
 *
 * "Active" mirrors the logic in BookingContextProvider.isBookingActive: now is within
 * [from, until]; for day-unit bookings the upper bound is extended to the next day's
 * `site.accessibleFrom` as a grace period; canceled bookings without occupancies do not count.
 */
export function findActiveBookingOnSite(
	bookings: B2Booking[],
	siteId: string,
	now: ZonedDateTime,
): B2Booking | undefined {
	return bookings.find(b => b.site.id === siteId && isBookingActive(b, now));
}

function isBookingActive(booking: B2Booking, now: ZonedDateTime): boolean {
	const zoneId = booking.site.zoneId ?? "UTC";
	const from = safeZoned(booking.from, zoneId);
	const until = safeZoned(booking.until, zoneId);
	if (!from || !until) return false;

	if (ZonedDateTime.compare(from, now) > 0) return false;

	if (booking.state === "Canceled" && (booking.occupancies ?? []).length === 0) {
		return false;
	}

	if (booking.site.bookingUnit === "Days") {
		const accessibleFrom = booking.site.accessibleFrom || "00:00";
		const [hourStr, minuteStr] = accessibleFrom.split(":");
		const hour = Number.parseInt(hourStr);
		const minute = Number.parseInt(minuteStr);
		if (Number.isFinite(hour) && Number.isFinite(minute)) {
			try {
				const graceEnd = until.add({days: 1}).with({hour, minute, second: 0, millisecond: 0});
				return ZonedDateTime.compare(graceEnd, now) >= 0;
			}
			catch {
				// fall through to strict window
			}
		}
	}

	return ZonedDateTime.compare(from, now) <= 0 && ZonedDateTime.compare(until, now) >= 0;
}

function safeZoned(instantOrIso: string, zoneId: string): ZonedDateTime | undefined {
	try {
		return ZonedDateTime.from(`${instantOrIso}[${zoneId}]`);
	}
	catch {
		return undefined;
	}
}
